import React, { FC } from 'react';
import classNames from 'classnames';
import { TFunction } from 'next-i18next';
import type { Reviews, Title } from 'src/common/types/api/v4/search/v1/landing';
import { CarouselProvider } from 'src/common/providers/CarouselProvider';
import CarouselButtons from 'src/common/components/dockyard/CarouselButtons';
import Carousel from 'src/common/components/dockyard/Carousel';
import SearchReview from 'src/common/components/SearchReview';
import Typography from 'src/common/components/Typography';
import { PATHS } from 'src/common/constants';
import { REVIEWS_ANCHOR } from 'src/search/constants';
import PitchPageLayout from 'src/common/components/PitchPageLayout';
import UniversalLink from 'src/common/components/UniversalLink';
import getClassNames from 'src/common/utils/getClassNames';
import styles from './LandingPageReviews.module.scss';

export type LandingPageReviewsProps = {
  clampLines?: boolean;
  headingSize: 'heading-3' | 'display-5';
  title: Title;
  reviews: Reviews['top_reviews'];
  translationFunction?: TFunction;
};

const LandingPageReviews: FC<LandingPageReviewsProps> = ({
  clampLines = true,
  headingSize,
  title,
  reviews,
  translationFunction,
}) => (
  <PitchPageLayout
    id={REVIEWS_ANCHOR}
    backgroundColor="ivory"
  >
    <div className={styles.root}>
      <CarouselProvider>
        <div className={styles.header}>
          <Typography
            type="h2"
            size={headingSize}
            weight="semibold"
          >
            {title}
          </Typography>
          <div className={styles.buttonsDesktop}>
            <CarouselButtons
              spacing="together"
              variant="primary-dark"
            />
          </div>
        </div>
        <div className={styles.carousel}>
          <Carousel>
            {reviews?.map((review) => (
              review.boat ? (
                <UniversalLink
                  href={`${PATHS.LISTING}${review.boat}`}
                  key={review.id}
                >
                  <ul className={getClassNames(styles, 'review', classNames({ clampLines }))}>
                    <SearchReview
                      author={review.user}
                      date={review.date_created}
                      content={
                          translationFunction
                            ? translationFunction(review.public_review)
                            : review.public_review
                        }
                      listingTitle={
                          translationFunction
                            ? translationFunction(review.listing_headline)
                            : review.listing_headline
                        }
                      rating={review.rating}
                      theme="dark"
                      size="small"
                    />
                  </ul>
                </UniversalLink>
              ) : (
                <ul key={review.id} className={styles.review}>
                  <SearchReview
                    author={review.user}
                    date={review.date_created}
                    content={
                        translationFunction
                          ? translationFunction(review.public_review)
                          : review.public_review
                      }
                    listingTitle={
                        translationFunction
                          ? translationFunction(review.listing_headline)
                          : review.listing_headline
                      }
                    rating={review.rating}
                    theme="dark"
                    size="small"
                  />
                </ul>
              )
            ))}
          </Carousel>
        </div>
        <div className={styles.buttonsMobile}>
          <CarouselButtons
            spacing="together"
            variant="primary-dark"
          />
        </div>
      </CarouselProvider>
    </div>
  </PitchPageLayout>
);

export default LandingPageReviews;
